import React from 'react';
import { useParams } from 'react-router-dom';
import { getDatabase, ref, onValue, push } from 'firebase/database';
import QRCode from 'react-qr-code';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import './AdmissionLetter.css';
import logo from '../pages/logo.png';
import PaymentReceipt from './PaymentReceipt';

// Type definitions
interface Student {
  uid: string;
  email: string;
  phone: string;
  fullName: string;
  regNo?: string;
  registrationNumber?: string;
  assignedClass: string;
  classSought?: string;
  session: string;
  status: string;
  imageUrl?: string;
  username?: string;
}

interface SchoolInfo {
  schoolName: string;
  motto: string;
  principalName: string;
  address: string;
  schoolFees: Record<string, Record<string, string | number>>;
  acceptanceFee: string | number;
}

interface ReceiptData {
  receiptNo: string;
  date: string;
  studentName: string;
  regNo: string;
  studentClass: string;
  session: string;
  paymentDescription: string;
  amount: string;
}

const AdmissionLetter = () => {
  const { studentId } = useParams<{ studentId: string }>();

  const [student, setStudent] = React.useState<Student | null>(null);
  const [schoolInfo, setSchoolInfo] = React.useState<SchoolInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [paymentSuccessful, setPaymentSuccessful] = React.useState(false);
  const [receiptData, setReceiptData] = React.useState<ReceiptData | null>(null);

  React.useEffect(() => {
    const db = getDatabase();
    let studentUnsubscribe: (() => void) | undefined = undefined;

    const fetchStudentData = () => {
      setLoading(true);
      setError('');

      if (studentId) {
        const studentRef = ref(db, `users/${studentId}`);
        studentUnsubscribe = onValue(studentRef, (snapshot) => {
            if (snapshot.exists()) {
                const studentData = snapshot.val();
                studentData.uid = snapshot.key;
                setStudent(studentData);
            } else {
                setError('No student data found for the provided ID.');
                setStudent(null);
            }
            setLoading(false);
        }, (err) => {
            setError(`Failed to fetch student data: ${err.message}`);
            setLoading(false);
        });
      } else {
        setError('No student identifier provided in the URL.');
        setLoading(false);
      }
    };

    fetchStudentData();

    const schoolInfoRef = ref(db, 'school');
    const schoolInfoUnsubscribe = onValue(schoolInfoRef, (snapshot) => {
      if (snapshot.exists()) setSchoolInfo(snapshot.val());
    });

    return () => {
      if (studentUnsubscribe) {
        studentUnsubscribe();
      }
      schoolInfoUnsubscribe();
    };
  }, [studentId]);

  const getSchoolFees = () => {
    if (!student || !schoolInfo?.schoolFees) return { fee: 0, acceptanceFee: 0 };
    const studentClass = student.assignedClass || student.classSought;
    if (!studentClass) return { fee: 0, acceptanceFee: 0 };

    const normalizedStudentClass = studentClass.replace(/\s+/g, '').toLowerCase();
    const feeClassKey = Object.keys(schoolInfo.schoolFees).find(key => key.replace(/\s+/g, '').toLowerCase() === normalizedStudentClass);
    
    if (!feeClassKey) return { fee: 0, acceptanceFee: 0 };

    const classFees = schoolInfo.schoolFees[feeClassKey];
    if (!classFees) return { fee: 0, acceptanceFee: 0 };
    
    const studentStatus = student.status?.toLowerCase() === 'new' ? 'new' : 'returning';

    let schoolFeeString = '0';
    if (studentStatus === 'new' && classFees.new) {
        schoolFeeString = String(classFees.new);
    } else {
        schoolFeeString = String(classFees.returning || '0');
    }
    const finalSchoolFee = parseFloat(schoolFeeString.replace(/,/g, '')) || 0;

    let finalAcceptanceFee = 0;
    if (studentStatus === 'new' && schoolInfo.acceptanceFee) {
        const acceptanceFeeString = String(schoolInfo.acceptanceFee);
        finalAcceptanceFee = parseFloat(acceptanceFeeString.replace(/,/g, '')) || 0;
    }
    
    return { fee: finalSchoolFee, acceptanceFee: finalAcceptanceFee };
  };

  const { fee, acceptanceFee } = getSchoolFees();
  const totalAmount = fee + acceptanceFee;

  const config = {
    public_key: 'FLWPUBK-de141ad267bc824bb5d40b9afd5f6b23-X',
    tx_ref: Date.now().toString(),
    amount: totalAmount,
    currency: 'NGN',
    payment_options: 'card,mobilemoney,ussd',
    customer: {
      email: student?.email || 'not-provided@example.com',
      phone_number: student?.phone || '',
      name: student?.fullName || '',
    },
    customizations: {
      title: 'Holy See Bright Academy Fees',
      description: 'Payment for School & Acceptance Fees',
      logo: 'https://holyseebright.com/logo.png',
    },
  };

  const handleFlutterwavePayment = useFlutterwave(config);

  const handlePayment = (e: any) => {
    e.preventDefault();

    handleFlutterwavePayment({
      callback: async (response) => {
        if (response.status === 'successful') {
          if (!student) {
            alert('Error: Student data is not available. Cannot process payment.');
            closePaymentModal();
            return;
          }
          
          const db = getDatabase();
          const paymentRef = await push(ref(db, 'payments'), {
            studentId: student.uid,
            amount: response.amount,
            date: new Date().toISOString(),
            txRef: response.tx_ref,
            status: 'successful',
          });

          if (paymentRef.key) {
            setReceiptData({
              receiptNo: paymentRef.key,
              date: new Date().toLocaleDateString('en-GB'),
              studentName: student.fullName,
              regNo: student.regNo || 'N/A',
              studentClass: student.assignedClass || student.classSought || 'N/A',
              session: student.session,
              paymentDescription: 'SCHOOL FEES & ACCEPTANCE FEE',
              amount: response.amount.toLocaleString(),
            });
            setPaymentSuccessful(true);
          } else {
            alert('Error: Failed to create a payment record key.');
          }

        } else {
          alert('Payment failed!');
        }
        closePaymentModal();
      },
      onClose: () => {},
    });
  };

  const getDuration = (className: string | undefined) => {
    if (!className) return 'N/A';
    const lower = className.toLowerCase();
    if (lower.startsWith('creche')) return '11 Years';
    if (lower.startsWith('pre-nursery')) return '10 Years';
    if (lower.startsWith('kg')) return '9 Years';
    if (lower.startsWith('nursery')) return '7 Years';
    if (lower.startsWith('primary')) return '6 Years';
    if (lower.startsWith('jss')) return '3 Years';
    if (lower.startsWith('sss')) return '3 Years';
    return 'N/A';
  };

  const verificationUrl = student ? `${window.location.origin}/verify-admission/${student.regNo}` : '';
  const academicSession = student?.session || `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`;

  if (loading) return <div className="wrap"><div>Loading Admission Letter...</div></div>;
  if (error) return <div className="wrap"><div className="alert alert-danger">{error}</div></div>;
  if (!student) return <div className="wrap"><div className="alert alert-info">No student data available.</div></div>;

  if (paymentSuccessful && receiptData) {
    return <PaymentReceipt receipt={receiptData} onClose={() => setPaymentSuccessful(false)} />;
  }

  return (
    <div className="wrap">
      <div className="admission-letter-scroll">
        <article className="page" role="document" aria-label="Admission Letter">
          <header>
            <img className="logo" src={logo} alt={schoolInfo?.schoolName || 'School'} />
            <img className="photo" src={student.imageUrl || 'https://via.placeholder.com/100x120.png?text=Photo'} alt={student.fullName || student.username} />
            <div className="center-head">
              <h1>{schoolInfo?.schoolName || 'HOLY SEE BRIGHT ACADEMY'}</h1>
              <div className="slogan">{schoolInfo?.motto || '... excellence and discipline in learning'}</div>
              <div className="session"><strong>ACADEMIC SESSION: {academicSession}</strong></div>
            </div>
          </header>

          <main className="content">
            <section className="meta">
              <div><b>APP. No:</b> {student.uid}</div>
              <div><b>Reg. No:</b> {student.regNo || 'N/A'}</div>
              <div><b>Dear:</b> {student.fullName || student.username}</div>
            </section>

            <h2 className="title">PROVISIONAL OFFER OF ADMISSION: {academicSession} ACADEMIC SESSION</h2>

            <p>This is to certify that you have met the admission requirements and you are successful. You are hereby offered a provisional admission as follows:</p>

            <p><b>Class:</b> {student.assignedClass || student.classSought}<br />
               <b>Duration of Course:</b> {getDuration(student.assignedClass || student.classSought)}
            </p>

            <p>You are required to present the following for documentation:</p>
            <ol>
              <li>First School Leaving Certificate (FSLC)</li>
              <li>Admission Acceptance Fee Receipt ({acceptanceFee > 0 ? `₦${acceptanceFee.toLocaleString()}` : 'N/A'})</li>
            </ol>

            <p>Complete ALL registration formalities within two weeks; failure attracts an extra charge of ₦5,000.</p>

            <div className="fee-summary">
              <h4>Fee breakdown:</h4>
              <p><strong>School Fees:</strong> ₦{fee.toLocaleString()}</p>
              {acceptanceFee > 0 && <p><strong>Acceptance Fee:</strong> ₦{acceptanceFee.toLocaleString()}</p>}
              <p><strong>Total:</strong> ₦{totalAmount.toLocaleString()}</p>
              <button className="btn btn-primary no-print" onClick={handlePayment} disabled={totalAmount <= 0}>
                {totalAmount > 0 ? 'Pay Now' : 'No Payment Required'}
              </button>
            </div>

            <div className="note">
              <div className="note-title">Note that:</div>
              <ol>
                <li>New Students are to bring two brooms.</li>
                <li>Compulsory English Language and Mathematics Textbooks for JSS 1 to SSS 3 and Compulsory English Language, Verbal Reasoning, Quantitative Reasoning and Mathematics Textbooks for KG 1 to PRIMARY FIVE.</li>
              </ol>
            </div>

            <div className="signature">
              <div className="sign-line"></div>
              <div className="sign-title">{schoolInfo?.principalName || 'Mrs. Christiana Emmanuel'}</div>
              <div>Principal</div>
            </div>

            <div className="verification-section">
              <div className="qr-code">
                <QRCode value={verificationUrl} size={80} />
              </div>
              <div className="small-link">
                {schoolInfo?.address || 'Holy See Bright Academy, No. 1 Emmanuel Street, Abuja, Nigeria.'}<br />
                <a href={verificationUrl}>{verificationUrl}</a>
              </div>
            </div>
          </main>
        </article>
      </div>
    </div>
  );
};

export default AdmissionLetter;
